import React, { memo, useState, useEffect, useRef, useId } from 'react';
import get from 'lodash/get';
import { css, useTheme } from '@emotion/react';
import type {
  ArrayEntryType,
  ConditionalContainerEntryType,
  DLClickEventHandlerProps,
  TabEntryType,
  TabsEntryType,
} from 'tsconfig/types';
import { Popover } from '@mantine/core';
import { useRouter } from 'next/compat/router';
import { MainLargeNavGroup } from './MainLargeNavGroup';
import { TypeBasedConditionalContent } from '../../contentful/CmsConditionalContainer/TypeBasedConditionalContent';
import { globals, inspectorMode, Link } from 'common-ui';

// Helper function outside the component to check if node is a content type
function isContentType(node: any): boolean {
  return node instanceof Object && node?.sys && node?.fields;
}

// Helper function to process field nodes
function processFieldNode(fieldNode: any, uniqueKey: string, action: any, iterateTree: any): void {
  if (Array.isArray(fieldNode)) {
    for (const arrayNode of fieldNode) {
      iterateTree(arrayNode, uniqueKey + arrayNode?.sys?.id, action);
    }
  } else if (fieldNode instanceof Object) {
    iterateTree(fieldNode, uniqueKey + fieldNode?.sys?.id, action);
  }
}

// Helper function to process node fields
function processNodeFields(node: any, uniqueKey: string, action: any, iterateTree: any): void {
  if (node?.fields && typeof node?.fields === 'object') {
    for (const key of Object.keys(node.fields)) {
      const fieldNode = node.fields[key];
      processFieldNode(fieldNode, uniqueKey, action, iterateTree);
    }
  }
}

export type MainNavBarProps = {
  id?: string;
  cmsMainNav?: ArrayEntryType<TabsEntryType>;
  globalHeaderHeight: number;
  dlClickEventHandler: DLClickEventHandlerProps;
};

type MainNavMenuItemProps = {
  tabPane: TabEntryType;
  index: number;
  componentId: string;
  openMenus: string[];
  globalHeaderHeight: number;
  dlClickEventHandler: DLClickEventHandlerProps;
  openMenu: (submenuId: string, delay?: number) => void;
  closeMenu: (submenuId: string) => void;
  scheduleCloseMenu: (submenuId: string) => void;
  handleTopLevelKeyDown: (event: React.KeyboardEvent<HTMLAnchorElement>) => void;
  inputIsPointerRef: React.MutableRefObject<boolean>;
};

const MainNavMenuItem = ({
  tabPane,
  index,
  componentId,
  openMenus,
  globalHeaderHeight,
  dlClickEventHandler,
  openMenu,
  closeMenu,
  scheduleCloseMenu,
  handleTopLevelKeyDown,
  inputIsPointerRef,
}: MainNavMenuItemProps) => {
  const theme = useTheme();
  const widgetTheme = theme.widgets.GlobalHeader || {};
  const tabPopup = css`
    ${widgetTheme.mainNavBarMenuTabPane}
    ${theme.mixins.containerLayoutFixed}
  `;

  if (!tabPane?.fields) {
    return null;
  }

  const { title, content, tabUrl = '#', emphasizeTitle, stickyContent } = tabPane.fields;
  const tabInstanceId = get(tabPane, 'sys.id');
  const tabTitleClass = emphasizeTitle ? 'megaNavTab emphasize' : 'megaNavTab';
  const submenuId = `main-nav-submenu-${componentId}-${tabInstanceId ?? index}`;
  const isOpen = openMenus.includes(submenuId);

  return content && content?.length > 0 ? (
    <Popover
      width="-webkit-fill-available"
      shadow="md"
      withinPortal={globals.brandId === 'newstf'}
      position="bottom"
      opened={isOpen}
      onClose={() => closeMenu(submenuId)}
    >
      <Popover.Target popupType="dialog">
        <div
          className="megaNavTabGroup"
          onMouseEnter={() => {
            inputIsPointerRef.current = true;
            openMenu(submenuId, 500);
          }}
          onMouseLeave={() => scheduleCloseMenu(submenuId)}
          onMouseDownCapture={() => {
            inputIsPointerRef.current = true;
          }}
          onKeyDownCapture={() => {
            inputIsPointerRef.current = false;
          }}
          onFocus={() => openMenu(submenuId)}
          onBlur={() => scheduleCloseMenu(submenuId)}
        >
          <Link
            className={tabTitleClass}
            href={tabUrl}
            aria-expanded={isOpen}
            aria-controls={submenuId}
            onKeyDown={handleTopLevelKeyDown}
            data-event="mainNavbarClick"
            data-section={`main navbar:${title}`}
            data-action="navigation"
            data-name="main navbar"
            data-text={title}
            data-nav-label={title}
            onClick={() => {
              closeMenu(submenuId);
              dlClickEventHandler(title);
            }}
            {...inspectorMode(tabInstanceId, 'title')}
          >
            <span>{title}</span>
          </Link>
        </div>
      </Popover.Target>
      <Popover.Dropdown
        id={submenuId}
        css={tabPopup}
        onMouseEnter={() => {
          inputIsPointerRef.current = true;
          openMenu(submenuId);
        }}
        onMouseLeave={() => scheduleCloseMenu(submenuId)}
        onMouseDownCapture={() => {
          inputIsPointerRef.current = true;
        }}
        onKeyDown={() => {
          inputIsPointerRef.current = false;
        }}
        onFocus={() => openMenu(submenuId)}
        onBlur={() => scheduleCloseMenu(submenuId)}
      >
        <div className="header-main-nav-layout">
          <MainLargeNavGroup
            globalHeaderHeight={globalHeaderHeight}
            menuContent={content}
            stickyContent={stickyContent}
            dlClickEventHandler={data => {
              dlClickEventHandler(`${title}:${data}`);
            }}
          />
        </div>
      </Popover.Dropdown>
    </Popover>
  ) : (
    <Link
      className={tabTitleClass}
      href={tabUrl}
      data-action="navigation"
      data-section={`main navbar:${title}`}
      data-name="main navbar"
      data-text={title}
      data-nav-label={title}
      onKeyDown={handleTopLevelKeyDown}
      onClick={() => dlClickEventHandler(title)}
      {...inspectorMode(tabInstanceId, 'title')}
    >
      <span>{title}</span>
    </Link>
  );
};

function renderConditionalNavItem(props: Omit<MainNavMenuItemProps, 'tabPane'>, content: TabEntryType) {
  return <MainNavMenuItem {...props} tabPane={content} />;
}

function renderConditionalNavGroup(
  renderTabs: (tabPanes: (TabEntryType | ConditionalContainerEntryType)[]) => React.ReactNode,
  content: TabsEntryType,
) {
  return (
    <ul className="topLevelNavList">
      {renderTabs(content?.fields?.tabPanes as (TabEntryType | ConditionalContainerEntryType)[])}
    </ul>
  );
}

export const MainNavBar = memo(({ cmsMainNav = [], id, globalHeaderHeight, dlClickEventHandler }: MainNavBarProps) => {
  const componentId = useId();
  const routerEvents = useRouter()?.events;
  const theme = useTheme();
  const widgetTheme = theme.widgets.GlobalHeader || {};
  const wrapper = css`
      ${widgetTheme.mainNavBarWrapper}
    `,
    seoMenu = css`
      display: none;
    `;
  const [openMenus, setOpenMenus] = useState<string[]>([]);
  const inputIsPointerRef = useRef(false);
  const closeTimeoutsRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const openTimeoutsRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  useEffect(() => {
    if (!routerEvents) {
      return;
    }
    const resetMenus = () => {
      Object.values(closeTimeoutsRef.current).forEach(timeoutId => clearTimeout(timeoutId));
      Object.values(openTimeoutsRef.current).forEach(timeoutId => clearTimeout(timeoutId));
      closeTimeoutsRef.current = {};
      openTimeoutsRef.current = {};
      setOpenMenus([]);
    };

    routerEvents.on('routeChangeComplete', resetMenus);
    routerEvents.on('routeChangeError', resetMenus);
    return () => {
      routerEvents.off('routeChangeComplete', resetMenus);
      routerEvents.off('routeChangeError', resetMenus);
    };
  }, [routerEvents]);

  useEffect(() => {
    const htmlDoc = document.getElementsByTagName('html')?.[0];
    const isAnyMenuOpen = openMenus.length > 0;
    const isSearchOverlayVisible = Array.from(document.querySelectorAll<HTMLElement>('.search-overlay')).some(
      searchOverlay => getComputedStyle(searchOverlay).display !== 'none',
    );
    if (isAnyMenuOpen) {
      htmlDoc.style.overflow = 'hidden';
    } else {
      htmlDoc.style.overflow = 'visible';
    }
    document
      .querySelector('.nav-overlay')
      ?.classList.toggle('active-nav-overlay', isAnyMenuOpen && !isSearchOverlayVisible);
  }, [openMenus]);

  useEffect(
    () => () => {
      Object.values(closeTimeoutsRef.current).forEach(timeoutId => clearTimeout(timeoutId));
      Object.values(openTimeoutsRef.current).forEach(timeoutId => clearTimeout(timeoutId));
      const htmlDoc = document.getElementsByTagName('html')?.[0];
      if (htmlDoc) {
        htmlDoc.style.overflow = 'visible';
      }
      document.querySelector('.nav-overlay')?.classList.remove('active-nav-overlay');
    },
    [],
  );

  const clearScheduledClose = (submenuId: string) => {
    if (closeTimeoutsRef.current[submenuId]) {
      clearTimeout(closeTimeoutsRef.current[submenuId]);
      delete closeTimeoutsRef.current[submenuId];
    }
  };

  const clearScheduledOpen = (submenuId: string) => {
    if (openTimeoutsRef.current[submenuId]) {
      clearTimeout(openTimeoutsRef.current[submenuId]);
      delete openTimeoutsRef.current[submenuId];
    }
  };

  const openMenu = (submenuId: string, delay = 0): void => {
    clearScheduledClose(submenuId);
    clearScheduledOpen(submenuId);
    if (delay > 0) {
      openTimeoutsRef.current[submenuId] = setTimeout(() => openMenu(submenuId), delay);
      return;
    }
    setOpenMenus((prev: string[]) => (prev.includes(submenuId) ? prev : [...prev, submenuId]));
  };

  const closeMenu = (submenuId: string) => {
    clearScheduledClose(submenuId);
    clearScheduledOpen(submenuId);
    setOpenMenus((prev: string[]) => prev.filter(openMenuId => openMenuId !== submenuId));
  };

  const scheduleCloseMenu = (submenuId: string) => {
    clearScheduledClose(submenuId);
    clearScheduledOpen(submenuId);
    closeTimeoutsRef.current[submenuId] = setTimeout(() => {
      delete closeTimeoutsRef.current[submenuId];
      const { activeElement } = document;
      const trigger = document.querySelector(`[aria-controls="${submenuId}"]`);
      const submenu = document.getElementById(submenuId);

      if (
        !inputIsPointerRef.current &&
        activeElement &&
        (trigger?.contains(activeElement) || submenu?.contains(activeElement))
      ) {
        return;
      }

      closeMenu(submenuId);
    }, 200);
  };

  const handleTopLevelKeyDown = (event: React.KeyboardEvent<HTMLAnchorElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
      return;
    }

    const navContainer = event.currentTarget.closest('.topLevelLinks');
    const navLinks = Array.from(navContainer?.querySelectorAll<HTMLAnchorElement>('.topLevelLink a.megaNavTab') ?? []);
    const currentIndex = navLinks.indexOf(event.currentTarget);
    if (currentIndex < 0 || navLinks.length < 2) {
      return;
    }

    event.preventDefault();
    const direction = event.key === 'ArrowRight' ? 1 : -1;
    const nextIndex = (currentIndex + direction + navLinks.length) % navLinks.length;
    navLinks[nextIndex].focus();
  };

  const renderTab = (tabPanes: (TabEntryType | ConditionalContainerEntryType)[]) =>
    tabPanes?.map((tab, index: number) => {
      const isTab = tab?.sys?.contentType?.sys?.id === 'tab';
      if (isTab && !(tab as TabEntryType)?.fields) {
        return null;
      }

      return (
        <li
          className="topLevelLink"
          key={`${(tab as TabEntryType)?.fields?.title || (tab as ConditionalContainerEntryType)?.fields?.entryTitle || index}`}
        >
          <TypeBasedConditionalContent<TabEntryType>
            content={tab}
            contentTypes={['tab']}
            fallback={renderConditionalNavItem.bind(null, {
              index,
              componentId,
              openMenus,
              globalHeaderHeight,
              dlClickEventHandler,
              openMenu,
              closeMenu,
              scheduleCloseMenu,
              handleTopLevelKeyDown,
              inputIsPointerRef,
            })}
            enableLoader={true}
          >
            <MainNavMenuItem
              tabPane={tab as TabEntryType}
              index={index}
              componentId={componentId}
              openMenus={openMenus}
              globalHeaderHeight={globalHeaderHeight}
              dlClickEventHandler={dlClickEventHandler}
              openMenu={openMenu}
              closeMenu={closeMenu}
              scheduleCloseMenu={scheduleCloseMenu}
              handleTopLevelKeyDown={handleTopLevelKeyDown}
              inputIsPointerRef={inputIsPointerRef}
            />
          </TypeBasedConditionalContent>
        </li>
      );
    }) || null;

  const renderMegaNavTabs = () =>
    (cmsMainNav as (TabsEntryType | ConditionalContainerEntryType)[])?.map(tabGroup => (
      <TypeBasedConditionalContent<TabsEntryType>
        content={tabGroup}
        contentTypes={['tabs']}
        key={`${tabGroup?.fields?.entryTitle}-mainNav`}
        fallback={renderConditionalNavGroup.bind(null, renderTab)}
        enableLoader={true}
      >
        <ul className="topLevelNavList">
          {renderTab((tabGroup as TabsEntryType)?.fields?.tabPanes as (TabEntryType | ConditionalContainerEntryType)[])}
        </ul>
      </TypeBasedConditionalContent>
    ));

  //The html generated by our mega nav (when using 'keepMounted' flag) results a bloated static page render. Instead
  //we defer the main render until client load BUT still render a minimal hidden 'SEO menu' so that nav links are
  //still present for SEO purposes
  function renderSEOMenu() {
    return (cmsMainNav as TabsEntryType[])?.map((tabs: TabsEntryType) => {
      const tabInstanceId = get(tabs, 'sys.id');
      const links: any[] = [];

      function iterateTree(node: any, uniqueKey: string, action: any) {
        if (isContentType(node)) {
          action(node, uniqueKey);
          processNodeFields(node, uniqueKey, action, iterateTree);
        }
      }

      iterateTree(tabs, tabInstanceId, (node: any, uniqueKey: string) => {
        const typeId = node?.sys?.contentType?.sys?.id;
        if (isContentType(node) && typeId === 'link') {
          links.push(
            <a key={uniqueKey} href={node?.fields?.url}>
              {node?.fields?.title}
            </a>,
          );
        }
      });

      return <div key={tabInstanceId}>{links}</div>;
    });
  }

  return (
    <div className="widget-MainNavBar" data-test="widget-MainNavBar" css={wrapper} id={id}>
      <div className="topLevelLinks">{renderMegaNavTabs()}</div>
      <div css={seoMenu}>{renderSEOMenu()}</div>
    </div>
  );
});
